import React, { useCallback, useEffect, useRef, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
  Alert,
  Link,
  Divider,
  Skeleton,
  IconButton,
} from "@mui/material";
import {
  Close as CloseIcon,
  RemoveShoppingCartOutlined,
} from "@mui/icons-material";
import TopProductListItem from "../../components/TopProductListItem/TopProductListItem";
import { useCart, getCartItemKey } from "../../context/cartContext";
import { useSite } from "../../context/siteContext";
import { useAuth } from "../../context/authContext";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { ASSETS_BASE_URL, DEFAULT_LAYOUT } from "../../config";
import { ROUTES } from "../../constants";
import { useTranslation } from "react-i18next";
import CartTable from "../../components/CartTable/CartTable";
import {
  getCartProductSummaries,
  getOptions,
  getCartRecommendations,
  getCartCTP,
} from "../../services/cartService";
import { getMachinesByIds } from "../../services/breadCrumbsService";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";
import CartUpload from "../../components/CartUpload/CartUpload";
import { toast } from "../../context/ToastContext";

// ─── Module-level image cache ────────────────────────────────────────────────
const imageCache = new Map();

const bomTreeCache = new Map();
const fetchBomTreeCached = async (machineId, siteNumber) => {
  const key = `${machineId}:${siteNumber}`;
  if (bomTreeCache.has(key)) return bomTreeCache.get(key);
  const promise = (async () => {
    try {
      const bomResponse = await getProductsByMachineId({ machine_id: machineId, site_number: siteNumber });
      if (!bomResponse?.status) {
        bomTreeCache.delete(key);
        return [];
      }
      const { bomTree } = normalizeMachineProductsResponse(bomResponse);
      return bomTree || [];
    } catch (error) {
      bomTreeCache.delete(key);
      return [];
    }
  })();
  bomTreeCache.set(key, promise);
  return promise;
};

const getNormalizedProductId = (item) =>
  item?.ProductID ?? item?.product_id ?? item?.productId ?? item?.id;
const toProductIdKey = (value) => String(value ?? "");
const normalizeScopeValue = (value) => {
  const normalized = String(value ?? "").trim();
  return normalized && normalized !== "undefined" && normalized !== "null" ? normalized : "";
};
const getScopeKey = (item = {}) => {
  const siteId = normalizeScopeValue(item?.site_id ?? item?.siteId);
  const cusId = normalizeScopeValue(item?.cus_id ?? item?.customer_id ?? item?.customerId);
  return siteId && cusId ? `${siteId}:${cusId}` : "";
};
const buildCartSyncSignature = (items = []) =>
  (items || [])
    .map((item) => {
      const key = getCartItemKey(item);
      const qty = Number(item?.Quantity || 0) || 0;
      return `${key}:${qty}`;
    })
    .sort()
    .join(",");
const findCartIndex = (items = [], productKey = "", site = "", cartKey = "") => {
  const resolvedCartKey = String(cartKey || "").trim();
  if (resolvedCartKey) {
    const exactIndex = items.findIndex(
      (item) => getCartItemKey(item) === resolvedCartKey && String(item?.Site) === String(site)
    );
    return exactIndex;
  }
  return items.findIndex(
    (item) =>
      toProductIdKey(getNormalizedProductId(item)) === toProductIdKey(productKey) &&
      String(item?.Site) === String(site)
  );
};
const getBomNodeLevel = (node = {}) => {
  const rawLevel = node?.bomLevel ?? node?.bom_level ?? node?.level;
  const parsedLevel = Number(rawLevel);
  return Number.isFinite(parsedLevel) && parsedLevel >= 0 ? parsedLevel : null;
};
const getBomNodeQuantity = (node = {}) => {
  const rawQty = node?.bomQuantity ?? node?.quantity ?? node?.qty ?? node?.bom_quantity ?? node?.bom_qty;
  const parsedQty = Number(rawQty);
  return Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;
};
const buildBomNodeDetailsMap = (bomTree = [], machineId) => {
  const details = new Map();
  const traverse = (nodes = [], parentNode = null, ancestorPath = []) => {
    nodes.forEach((node) => {
      const productId = Number(node?.productId);
      const currentNodeName = node?.productName || node?.product_name || "";
      const nextAncestorPath = currentNodeName ? [...ancestorPath, currentNodeName] : ancestorPath;
      if (Number.isFinite(productId) && productId > 0) {
        const key = `${machineId}:${productId}`;
        const bomLevel = getBomNodeLevel(node);
        const isTopLevelPart = Number(bomLevel) === 1;
        const parentName = isTopLevelPart ? "" : (parentNode?.productName || parentNode?.product_name || "");
        const parentPath = isTopLevelPart ? "" : ancestorPath.join(" > ");
        const bomRef = {
          bom_level: bomLevel,
          bom_qty: getBomNodeQuantity(node),
          assembly_name: parentName,
          bom_path: parentPath,
        };
        const existing = details.get(key);
        if (!existing) details.set(key, { bom_level: bomLevel, bom_refs: [bomRef] });
        else details.set(key, { ...existing, bom_refs: [...(existing.bom_refs || []), bomRef] });
      }
      if (Array.isArray(node?.children) && node.children.length > 0) {
        traverse(node.children, node, nextAncestorPath);
      }
    });
  };
  traverse(bomTree, null, []);
  return details;
};

// ─── CartModal Component ─────────────────────────────────────────────────────
const CartModal = ({ open, onClose }) => {
  const { cartItems, removeFromCart, clearCart, updateCart, isInitialized, refreshCart, maxCartLines: maxCartLinesFromContext } =
    useCart();
  const maxCartLines = Number(maxCartLinesFromContext) || 4000;
  const { selectedSite, selectedSiteCurrency, currencySymbol, selectedSiteData, selectedSiteKey } = useSite();
  const { user, hasPermission } = useAuth();
  const { stockImageUrl } = useBrandingAssets();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cartProducts, setCartProducts] = useState([]);
  const [isLocalInitialized, setIsLocalInitialized] = useState(false);
  const [recommendedProducts, setRecommendedProducts] = useState([]);
  const [showDisclaimerCheckout, setShowDisclaimerCheckout] = useState(false);
  const [quantityInputs, setQuantityInputs] = useState({});
  const [pendingMixedSummary, setPendingMixedSummary] = useState(null);
  const [appOptions, setAppOptions] = useState({
    currency: "USD",
    minOrderAmount: 0,
    maxOrderQty: 999,
    creditCardFee: 2.5,
    taxRate: 5,
    shippingCost: 10,
    leadTime: 3,
  });
  const hasLoadedOptionsRef = useRef(false);

  const tableScrollRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const ctpDebounceRef = useRef(null);
  const ctpReqSeq = useRef(0);
  const recDebounceRef = useRef(null);
  const recReqSeq = useRef(0);

  const { t } = useTranslation();
  const maxQtyAllowed = 999;
  const canPlaceOrder = hasPermission("order_management.create_order");
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Lock body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      refreshCart();
    }
  }, [open, refreshCart]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const triggerToast = (message, severity = "info") => {
    if (!message) return;
    toast.trigger({ message, severity });
  };

  const handleNavigateToProduct = useCallback((url) => {
    onCloseRef.current();
    navigateRef.current(url);
  }, []);

  const resolveProductImage = (productNumber, imagePath) => {
    if (!imagePath && !productNumber)
      return stockImageUrl || `${ASSETS_BASE_URL}/stock.png`;
    if (imageCache.has(productNumber)) return imageCache.get(productNumber);
    const productImageUrl = imagePath
      ? `${ASSETS_BASE_URL}/${encodeURI(String(imagePath).replace(/^\/+/, ""))}`
      : `${ASSETS_BASE_URL}/ProductImages/${productNumber}.png`;
    imageCache.set(productNumber, productImageUrl);
    return productImageUrl;
  };

  const productsInCart = useMemo(() => {
    if (selectedSite) {
      return (
        cartItems?.filter(
          (item) => { 
          if (item === null || item === undefined) return false;
          const selectedScopeKey = String(selectedSiteKey || "").trim() ||
            ((selectedSiteData?.site_id && selectedSiteData?.cus_id)
              ? `${selectedSiteData.site_id}:${selectedSiteData.cus_id}`
              : "");
          const itemSiteId = String(item?.site_id ?? item?.siteId ?? "").trim();
          const itemCusId = String(item?.cus_id ?? item?.customer_id ?? item?.customerId ?? "").trim();
          const itemScopeKey = itemSiteId && itemCusId ? `${itemSiteId}:${itemCusId}` : "";
          if (selectedScopeKey && itemScopeKey) return itemScopeKey === selectedScopeKey;
          if (selectedScopeKey && !itemScopeKey) return false;
          return String(item?.Site ?? "") === String(selectedSite ?? "");
        }
        ) || []
      );
    }
    return [];
  }, [cartItems, selectedSite, selectedSiteData?.site_id, selectedSiteData?.cus_id, selectedSiteKey]);

  // Parts can be added freely; this number only matters when submitting the quote, so it stays
  // neutral until the quote is actually over the limit.
  const partsInQuote = productsInCart.length;
  const isOverPartLimit = partsInQuote > maxCartLines;
  const cartCapacityColor = isOverPartLimit ? "error.main" : "text.secondary";

  const uniqueProductIds = useMemo(
    () => [
      ...new Set(
        productsInCart.map((p) => getNormalizedProductId(p)).filter(Boolean)
      ),
    ],
    [productsInCart]
  );

  const cartProductIds = useMemo(
    () => uniqueProductIds.slice().sort().join(","),
    [uniqueProductIds]
  );
  const cartItemsSignature = useMemo(
    () => buildCartSyncSignature(productsInCart),
    [productsInCart]
  );
  const localCartSignature = useMemo(
    () => buildCartSyncSignature(cartProducts),
    [cartProducts]
  );

  useEffect(() => {
    setQuantityInputs({});
  }, [cartItemsSignature]);

  const fetchCTPForItems = async (cartItems) => {
    try {
      const req = {
        items: cartItems.map((cp) => ({
          product_id: getNormalizedProductId(cp),
          quantity: cp?.Quantity || 1,
        })),
        site_number: selectedSite,
      };
      const resp = await getCartCTP(req);
      const data = (resp?.data || []).filter((d) => d !== null && d !== undefined);
      const byId = new Map(data.map((d) => [String(d.product_id), d.ctp]));
      return cartItems
        .map((cp) => {
          if (!cp) return null;
          const pid = String(getNormalizedProductId(cp));
          const ctp = byId.get(pid);
          return ctp ? { ...cp, ctp } : cp;
        })
        .filter((cp) => cp !== null);
    } catch (e) {
      console.error("Failed to fetch CTP:", e);
      return cartItems;
    }
  };

  const scheduleCTPRefresh = (items) => {
    if (ctpDebounceRef.current) clearTimeout(ctpDebounceRef.current);
    const seq = ++ctpReqSeq.current;
    ctpDebounceRef.current = setTimeout(async () => {
      try {
        const withCTP = await fetchCTPForItems(items);
        if (seq === ctpReqSeq.current) {
          setCartProducts(withCTP);
        }
      } catch (e) {
        // ignore stale responses
      }
    }, 800);  // ─── FIX #4: Increased debounce from 400ms to 800ms ──────────────────────
              // Prevents CTP refresh from firing mid-scroll. Still imperceptible to users.
  };

  const fetchOptions = async () => {
    try {
      const response = await getOptions();
      setAppOptions(response.data.options);
      hasLoadedOptionsRef.current = true;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchProducts = async () => {
    setIsLocalInitialized(false);
    setLoading(true);
    setError(null);
    const optionsPromise = hasLoadedOptionsRef.current
      ? Promise.resolve()
      : fetchOptions();

    if (productsInCart.length === 0) {
      setCartProducts([]);
      setLoading(false);
      setIsLocalInitialized(true);
      return;
    }

    try {
      const productIds = uniqueProductIds;
      const [response] = await Promise.all([
        getCartProductSummaries(productIds, selectedSite, selectedSiteData?.cus_id),
        optionsPromise,
      ]);
      const productData = (response?.data || []).filter((p) => p !== null && p !== undefined);
      const productById = new Map(
        productData
          .filter((product) => product?.product_id !== null && product?.product_id !== undefined)
          .map((product) => [Number(product.product_id), product])
      );
      const machineIds = [
        ...new Set(
          productData
            .map((product) => Number(product?.machine_id))
            .filter((machineId) => Number.isFinite(machineId) && machineId > 0)
        ),
      ];

      const getEffectiveLookupMachineId = (item) => {
        const matchedProduct = productById.get(Number(getNormalizedProductId(item)));
        return Number(matchedProduct?.machine_id || item?.machine_id || item?.machineId);
      };
      const machineIdsNeedingDetails = machineIds.filter((machineId) =>
        productsInCart.some((item) => {
          return getEffectiveLookupMachineId(item) === machineId && !(item?.machine_number || item?.machineNumber);
        })
      );
      const machineIdsNeedingBomFallback = machineIds.filter((machineId) =>
        productsInCart.some((item) => {
          const hasBomRefs = Array.isArray(item?.bom_refs) && item.bom_refs.length > 0;
          return getEffectiveLookupMachineId(item) === machineId && !hasBomRefs;
        })
      );

      const [machineDetailsResponse, bomNodeDetailEntries] = await Promise.all([
        machineIdsNeedingDetails.length
          ? getMachinesByIds({
              machines: machineIdsNeedingDetails,
              site_number: selectedSite,
              site_id: selectedSiteData?.site_id,
              cus_id: selectedSiteData?.cus_id,
            })
          : Promise.resolve(null),
        machineIdsNeedingBomFallback.length
          ? Promise.all(
              machineIdsNeedingBomFallback.map(async (machineId) => {
                const bomTree = await fetchBomTreeCached(machineId, selectedSite);
                return buildBomNodeDetailsMap(bomTree, machineId);
              })
            )
          : Promise.resolve([]),
      ]);
      const bomNodeDetailsMap = bomNodeDetailEntries.reduce((acc, entry) => {
        if (!entry) return acc;
        entry.forEach((value, key) => acc.set(key, value));
        return acc;
      }, new Map());
      const findAnyBomNodeDetails = (productId) => {
        if (!Number.isFinite(productId)) return null;
        for (const [key, value] of bomNodeDetailsMap.entries()) {
          if (String(key).endsWith(`:${productId}`)) return value;
        }
        return null;
      };
      const machineDetailsMap = new Map(
        (machineDetailsResponse?.data || [])
          .filter((machine) => Number.isFinite(Number(machine?.machine_id)))
          .map((machine) => [Number(machine.machine_id), machine])
      );

      let disclaimerFound = false;
      const updatedCartItems = await Promise.all(
        productsInCart.map(async (item) => {
          if (!item) return null;
          const matchedProduct = productById.get(Number(getNormalizedProductId(item)));
          if (matchedProduct) {
            const imageSrc = resolveProductImage(
              matchedProduct.product_number,
              matchedProduct.image_path
            );
            const showDisclaimer = ["30AA", "30BA", "3AAA", "3ABA"].includes(
              matchedProduct.product_class
            );
            const machineId = Number(
              matchedProduct?.machine_id || item?.machine_id || item?.machineId
            );
            const machineData =
              Number.isFinite(machineId) && machineId > 0
                ? machineDetailsMap.get(machineId)
                : null;
            const productId = Number(getNormalizedProductId(item));
            const bomNodeDetails =
              Number.isFinite(machineId) && Number.isFinite(productId)
                ? bomNodeDetailsMap.get(`${machineId}:${productId}`)
                : null;
            const fallbackBomNodeDetails =
              bomNodeDetails || (Number.isFinite(productId) ? findAnyBomNodeDetails(productId) : null);
            const lineNameFromUrl = new URLSearchParams(window.location.search).get("line_name") || "";
            if (showDisclaimer) disclaimerFound = true;
            return {
              ...item,
              CartKey:
                String(item?.CartKey || "").trim() ||
                getCartItemKey(item),
              product_data: matchedProduct,
              machineData: machineData || item?.machineData || null,
              machine_number:
                item?.machine_number ||
                item?.machineNumber ||
                machineData?.machine_number ||
                matchedProduct?.machine_number ||
                "",
              line_name:
                item?.line_name ||
                item?.lineName ||
                machineData?.line_name ||
                matchedProduct?.line_name ||
                lineNameFromUrl ||
                "",
              showDisclaimer,
              imageSrc,
              bom_level: item?.bom_level ?? fallbackBomNodeDetails?.bom_level ?? matchedProduct?.bom_level ?? null,
              bom_refs:
                Array.isArray(item?.bom_refs) && item.bom_refs.length > 0
                  ? item.bom_refs
                  : fallbackBomNodeDetails?.bom_refs || [],
            };
          }
          return null;
        })
      );

      if (disclaimerFound) setShowDisclaimerCheckout(true);
      const finalCartItems = updatedCartItems.filter((item) => item !== null);

      if (finalCartItems.length > 0) {
        setCartProducts(finalCartItems);
        setLoading(false);
        setIsLocalInitialized(true);
        scheduleCTPRefresh(finalCartItems);
      } else {
        setCartProducts([]);
        setLoading(false);
        setIsLocalInitialized(true);
      }
    } catch (err) {
      setError(t("ERROR_MESSAGES.FAILED_TO_FETCH_DATA", { error: err.message }));
      setCartProducts([]);
      setLoading(false);
      setIsLocalInitialized(true);
    }
  };

  useEffect(() => {
    if (!open || !isInitialized) return;
    if (!cartProductIds.length) {
      setCartProducts([]);
      setLoading(false);
      setIsLocalInitialized(true);
      return;
    }
    if (localCartSignature && localCartSignature === cartItemsSignature) {
      return;
    }
    fetchProducts();
  }, [cartProductIds, cartItemsSignature, localCartSignature, isInitialized, open]);

  // Recommendations
  useEffect(() => {
    if (!open || !isInitialized) return;
    if (!cartProductIds.length || !selectedSite) {
      setRecommendedProducts([]);
      return;
    }
    if (recDebounceRef.current) clearTimeout(recDebounceRef.current);
    const seq = ++recReqSeq.current;
    recDebounceRef.current = setTimeout(async () => {
      try {
        const response = await getCartRecommendations(
          uniqueProductIds,
          selectedSite,
          selectedSiteData?.cus_id,
          selectedSiteData?.site_id
        );
        if (seq !== recReqSeq.current) return;
        const recommendations = Array.isArray(response?.data) ? response.data : [];
        const uniqueRecommendations = [
          ...new Map(recommendations.map((item) => [item.pnum, item])).values(),
        ];
        setRecommendedProducts(uniqueRecommendations);
      } catch {
        if (seq !== recReqSeq.current) return;
        setRecommendedProducts([]);
      }
    }, 400);
  }, [cartProductIds, isInitialized, selectedSite, open]);

  const groupedCartProducts = useMemo(() => {
    if (!cartProducts.length) return {};
    return cartProducts.reduce((groups, item) => {
      if (!item || !item.product_data) return groups;
      const { ctp } = item;
      let shippingDate = null;
      if (ctp?.in_stock && ctp.in_stock.qty > 0) {
        shippingDate = ctp.in_stock.shipping_date;
      } else if (ctp?.out_of_stock && ctp?.out_of_stock?.qty > 0) {
        shippingDate = ctp.out_of_stock.shipping_date;
      } else {
        shippingDate = "__loading__";
      }
      if (!shippingDate) shippingDate = "__loading__";
      if (!groups[shippingDate]) groups[shippingDate] = [];
      groups[shippingDate].push(item);
      return groups;
    }, {});
  }, [cartProducts]);

  useEffect(() => {
    return () => {
      if (ctpDebounceRef.current) clearTimeout(ctpDebounceRef.current);
      if (recDebounceRef.current) clearTimeout(recDebounceRef.current);
    };
  }, []);

  const parsePrice = (price) => {
    const numPrice = typeof price === "string" ? parseFloat(price) : price;
    return isNaN(numPrice) ? 0 : numPrice;
  };

  const subtotal = useMemo(() => {
    return (cartProducts || []).reduce((total, item) => {
      if (!item || !item.product_data) return total;
      const key = `price_${selectedSiteCurrency}`;
      const currencyPrice = item.product_data?.[key];
      const finalPrice = item.product_data?.purchasable ? currencyPrice : 0;
      const price = parsePrice(finalPrice);
      const quantity = Number(item?.Quantity || 1);
      return total + price * quantity;
    }, 0);
  }, [cartProducts, selectedSiteCurrency]);

  const tax = useMemo(() => subtotal * 0, [subtotal]);
  const total = useMemo(() => subtotal + tax, [subtotal, tax]);

  const latest = useRef({});
  latest.current = {
    cartItems,
    cartProducts,
    quantityInputs,
    selectedSite,
    maxQtyAllowed,
    t,
    updateCart,
    removeFromCart,
    scheduleCTPRefresh,
    triggerToast,
  };

  const handleInputChange = useCallback((productId, inputValue) => {
    setQuantityInputs((prev) => ({ ...prev, [productId]: inputValue }));
  }, []);

  const handleInputBlur = useCallback((productId, cartKey = "") => {
    const { cartItems, cartProducts, quantityInputs, selectedSite, maxQtyAllowed, t } = latest.current;
    const productKey = toProductIdKey(productId);
    const resolvedCartKey = String(cartKey || "").trim();
    const inputValue = quantityInputs[productId];
    const currentItemIndex = findCartIndex(cartItems, productKey, selectedSite, resolvedCartKey);
    const currentItem = currentItemIndex > -1 ? cartItems[currentItemIndex] : null;
    const currentProductIndex = findCartIndex(cartProducts, productKey, selectedSite, resolvedCartKey);
    const currentProduct = currentProductIndex > -1 ? cartProducts[currentProductIndex] : null;
    const currentQuantity = Number(currentItem?.Quantity || currentProduct?.Quantity || 1) || 1;

    if (inputValue === undefined) {
      setQuantityInputs((prev) => ({ ...prev, [productId]: currentQuantity.toString() }));
      return;
    }

    const digitsOnly = inputValue ? inputValue.replace(/\D/g, "") : "";
    if (!digitsOnly) {
      setQuantityInputs((prev) => ({ ...prev, [productId]: currentQuantity.toString() }));
      return;
    }
    let quantity = parseInt(digitsOnly);
    if (isNaN(quantity)) {
      setQuantityInputs((prev) => ({ ...prev, [productId]: "1" }));
      updateQuantityRef.current(productId, 1, "input", resolvedCartKey);
      return;
    }
    let finalQuantity = quantity;
    let showAlert = false;
    let alertMessage = "";
    if (quantity < 1) {
      finalQuantity = 1;
      showAlert = true;
      alertMessage = t("ALERT_MESSAGES.QUANTITY_CANNOT_BE_LESS_THAN_ONE");
    } else if (quantity > maxQtyAllowed) {
      finalQuantity = maxQtyAllowed;
      showAlert = true;
      alertMessage = t("ALERT_MESSAGES.QUANTITY_ADJUSTED_TO_MAX", { maxStock: maxQtyAllowed });
    }
    setQuantityInputs((prev) => ({ ...prev, [productId]: finalQuantity.toString() }));
    updateQuantityRef.current(productId, finalQuantity, "input", resolvedCartKey);
    if (showAlert) latest.current.triggerToast(alertMessage, "warning");
  }, []);

  const handleKeyPress = useCallback((productId, event, cartKey = "") => {
    const { cartItems, selectedSite } = latest.current;
    const productKey = toProductIdKey(productId);
    const resolvedCartKey = String(cartKey || "").trim();
    if (event.key === "Enter") {
      event.target.blur();
    } else if (event.key === "Escape") {
      const currentItemIndex = findCartIndex(cartItems, productKey, selectedSite, resolvedCartKey);
      const currentItem = currentItemIndex > -1 ? cartItems[currentItemIndex] : null;
      const currentQuantity = currentItem ? currentItem.Quantity : 1;
      setQuantityInputs((prev) => ({ ...prev, [productId]: currentQuantity.toString() }));
      event.target.blur();
    }
  }, []);

  const handleKeyDown = useCallback((productId, event, cartKey = "") => {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      handleQuantityChangeRef.current(productId, 1, cartKey);
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      handleQuantityChangeRef.current(productId, -1, cartKey);
      return;
    }

    const allowedKeys = [
      "Backspace", "Delete", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "Tab", "Enter", "Escape",
    ];
    const isDigit = /^[0-9]$/.test(event.key);
    const isCtrlKey = event.ctrlKey || event.metaKey;
    if (!isDigit && !allowedKeys.includes(event.key) && !isCtrlKey) {
      event.preventDefault();
    }
  }, []);

  const handleRemoveItem = useCallback((productId, cartKey = "") => {
    const { removeFromCart } = latest.current;
    const productKey = toProductIdKey(productId);
    const resolvedCartKey = String(cartKey || "").trim();
    removeFromCart(productKey, resolvedCartKey);
    setCartProducts((prev) =>
      prev.filter((item) =>
        resolvedCartKey ? getCartItemKey(item) !== resolvedCartKey : toProductIdKey(getNormalizedProductId(item)) !== productKey
      )
    );
    setQuantityInputs((prev) => {
      const newState = { ...prev };
      delete newState[productId];
      return newState;
    });
  }, []);

  const handleQuantityChange = useCallback((product_id, count, cartKey = "") => {
    const { cartItems, selectedSite } = latest.current;
    const productKey = toProductIdKey(product_id);
    const resolvedCartKey = String(cartKey || "").trim();
    const currentItemIndex = findCartIndex(cartItems, productKey, selectedSite, resolvedCartKey);
    const currentItem = currentItemIndex > -1 ? cartItems[currentItemIndex] : null;
    if (!currentItem) return;
    const newQuantity = parseInt(currentItem.Quantity || 0, 10) + count;
    const source = count > 0 ? "increment" : "decrement";
    updateQuantityRef.current(productKey, newQuantity, source, resolvedCartKey);
  }, []);

  const handleQuantityChangeRef = useRef(handleQuantityChange);
  handleQuantityChangeRef.current = handleQuantityChange;

  const updateQuantity = async (productId, newQuantity, source = "direct", cartKey = "") => {
    const {
      cartItems,
      cartProducts,
      selectedSite,
      maxQtyAllowed,
      t,
      updateCart,
      scheduleCTPRefresh,
      triggerToast,
    } = latest.current;
    const productKey = toProductIdKey(productId);
    const resolvedCartKey = String(cartKey || "").trim();
    const cartItemIndex = findCartIndex(cartItems, productKey, selectedSite, resolvedCartKey);
    const productIndex = findCartIndex(cartProducts, productKey, selectedSite, resolvedCartKey);
    if (cartItemIndex < 0 || productIndex < 0) return;

    let finalQuantity = newQuantity;
    if (finalQuantity < 1) {
      finalQuantity = 1;
    } else if (finalQuantity > maxQtyAllowed) {
      finalQuantity = maxQtyAllowed;
      if (source === "increment") {
        triggerToast(
          t("ALERT_MESSAGES.MAX_STOCK_LIMIT_REACHED", { maxStock: maxQtyAllowed }),
          "info"
        );
      } else if (source === "input") {
        triggerToast(
          t("ALERT_MESSAGES.MAX_UNITS_ALLOWED_ADJUSTED", { maxStock: maxQtyAllowed }),
          "warning"
        );
      }
    }

    const updatedCartItems = [...cartItems];
    updatedCartItems[cartItemIndex] = { ...updatedCartItems[cartItemIndex], Quantity: finalQuantity };
    const updatedCartProducts = [...cartProducts];
    updatedCartProducts[productIndex] = { ...updatedCartProducts[productIndex], Quantity: finalQuantity };

    setCartProducts(updatedCartProducts);
    scheduleCTPRefresh(updatedCartProducts);
    updateCart(updatedCartItems);
    setQuantityInputs((prev) => ({ ...prev, [productId]: finalQuantity.toString() }));
  };

  const updateQuantityRef = useRef(updateQuantity);
  updateQuantityRef.current = updateQuantity;

  const handleCheckout = () => {
    onClose();
    navigate(ROUTES.CHECKOUT);
  };

  // Don't render anything if modal is not open
  if (!open) return null;

  const showLoadingState = loading || (productsInCart.length > 0 && cartProducts.length === 0);

  return (
    <>
      {/* ── Backdrop ─────────────────────────────────────────────────────────── */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1300,
          backdropFilter: "blur(2px)",
          animation: "fadeIn 0.2s ease",
          "@keyframes fadeIn": { from: { opacity: 0 }, to: { opacity: 1 } },
        }}
      />

      {/* ── Modal Panel ──────────────────────────────────────────────────────── */}
      <Box
        role="dialog"
        aria-modal="true"
        aria-label="Quote Cart"
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 1400,
          width: { xs: "98vw", sm: "95vw", md: "92vw", lg: "88vw" },
          maxWidth: "1300px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          backgroundColor: "background.paper",
          borderRadius: "12px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
          overflow: "hidden",
          animation: "slideUp 0.25s ease",
          "@keyframes slideUp": {
            from: { opacity: 0, transform: "translate(-50%, -46%)" },
            to: { opacity: 1, transform: "translate(-50%, -50%)" },
          },
        }}
      >
        {/* ── Modal Header ─────────────────────────────────────────────────── */}
        {/* CHANGE 1: "QUOTE" text is now centered using position absolute trick */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 3,
            py: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            backgroundColor: "background.paper",
          }}
        >
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ letterSpacing: "0.05em", textTransform: "uppercase" }}
          >
            {t("UI_TEXT.TOOLTIP_CART") || "QUOTE"}
          </Typography>
          <IconButton
            onClick={onClose}
            size="small"
            aria-label="Close cart"
            sx={{
              position: "absolute",
              right: 16,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
                color: "text.primary",
              },
              transition: "all 0.15s ease",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* ── Modal Body ───────────────────────────────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          {/* ── Scrollbar styles ─────────────────────────────────────────── */}
          <style>{`
            .cart-modal-scroll::-webkit-scrollbar { width: 6px; }
            .cart-modal-scroll::-webkit-scrollbar-track { background: transparent; }
            .cart-modal-scroll::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.18); border-radius: 3px; }
            .cart-modal-scroll::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.32); }
          `}</style>

          {/* ── Loading Skeleton ─────────────────────────────────────────── */}
          {showLoadingState && (
            <Box sx={{ flex: 1, p: 3, overflow: "hidden" }}>
              <Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: "10px" }}>
                <CardContent>
                  <Skeleton variant="text" width={220} height={28} />
                  <Divider sx={{ my: 2 }} />
                  {[...Array(3)].map((_, idx) => (
                    <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 2, py: 1.5 }}>
                      <Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: 1 }} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="60%" height={22} />
                        <Skeleton variant="text" width="40%" height={18} />
                      </Box>
                      <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
                      <Skeleton variant="text" width={80} height={22} />
                      <Skeleton variant="rectangular" width={32} height={32} sx={{ borderRadius: "50%" }} />
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Box>
          )}

          {/* ── Error State ──────────────────────────────────────────────── */}
          {!showLoadingState && error && (
            <Box sx={{ p: 3, flex: 1 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          {/* ── Empty Cart ───────────────────────────────────────────────── */}
          {!showLoadingState && !error && isLocalInitialized && cartProducts.length === 0 && productsInCart.length === 0 && (
            <Box sx={{ p: 3, flex: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                {t("ALERT_MESSAGES.CART_MESSAGE")}
              </Alert>
              <CartUpload onSummary={setPendingMixedSummary} />
            </Box>
          )}

          {/* ── Cart Content ─────────────────────────────────────────────── */}
          {!showLoadingState && !error && isLocalInitialized && (cartProducts.length > 0 || productsInCart.length > 0) && (
            <Box
              className="cart-modal-scroll"
              sx={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                px: { xs: 2, sm: 3 },
                py: 2.5,
              }}
            >
              {/* ── Part capacity indicator ───────────────────────────── */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1.5,
                  px: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontSize: "12px", fontWeight: 600, color: cartCapacityColor }}
                >
                  {partsInQuote.toLocaleString()} of {maxCartLines.toLocaleString()} parts
                </Typography>
              </Box>

              {/* ── Cart Table ─────────────────────────────────────────── */}
              <Card sx={{ boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)", borderRadius: "10px" }}>
                <CardContent sx={{ p: "16px !important" }}>
                  <Box
                    className="cart-modal-scroll"
                    ref={tableScrollRef}
                    sx={{
                      maxHeight: "calc(90vh * 0.55 - 80px)",
                      overflowY: "auto",
                      overflowX: "hidden",
                      pr: 0.5,
                    }}
                  >
                    <CartTable
                      scrollContainerRef={tableScrollRef}
                      groupedCartProducts={groupedCartProducts}
                      cartItems={cartItems}
                      selectedSite={selectedSite}
                      selectedSiteCurrency={selectedSiteCurrency}
                      stockImageSrc={stockImageUrl || `${ASSETS_BASE_URL}/stock.png`}
                      maxQtyAllowed={maxQtyAllowed}
                      quantityInputs={quantityInputs}
                      handleInputChange={handleInputChange}
                      handleInputBlur={handleInputBlur}
                      handleKeyPress={handleKeyPress}
                      handleKeyDown={handleKeyDown}
                      handleQuantityChange={handleQuantityChange}
                      handleRemoveItem={handleRemoveItem}
                      parsePrice={parsePrice}
                      handleNavigateToProduct={handleNavigateToProduct}
                      t={t}
                    />
                  </Box>
                </CardContent>
              </Card>

              {/* ── CHANGE 2 & 3: Action Row restructured ──────────────────
                  Row 1: Clear Quote + RFQ/Checkout buttons (side by side)
                  Row 2: Add Parts to Quote (full width below)
              ────────────────────────────────────────────────────────────── */}

              {/* Row 1: Buttons */}
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<RemoveShoppingCartOutlined />}
                  onClick={() => {
                    clearCart();
                    setCartProducts([]);
                    setQuantityInputs({});
                  }}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {t("BUTTON.CLEAR_CART")}
                </Button>

                <Button
                  variant="contained"
                  color="primary"
                  data-tour="cart-checkout-button"
                  onClick={handleCheckout}
                  disabled={
                    !canPlaceOrder ||
                    isOverPartLimit ||
                    total.toFixed(2) < (parseFloat(appOptions.minOrderAmount) || 0)
                  }
                  sx={{
                    py: 1,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    borderRadius: "8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("BUTTON.CHECKOUT")}
                </Button>
              </Box>

              {/* Row 2: Add Parts to Quote (full width) */}
              <Box sx={{ mt: 2 }}>
                <CartUpload onSummary={setPendingMixedSummary} />
              </Box>

              {/* ── Validation / Disclaimer messages ───────────────────── */}
              {isOverPartLimit && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  This quote has {partsInQuote.toLocaleString()} parts, which is over the{" "}
                  {maxCartLines.toLocaleString()} part limit. Please remove{" "}
                  {(partsInQuote - maxCartLines).toLocaleString()} part
                  {partsInQuote - maxCartLines === 1 ? "" : "s"} to continue.
                </Typography>
              )}
              {total.toFixed(2) < (parseFloat(appOptions.minOrderAmount) || 0) && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {t("UI_TEXT.MINIMUM_ORDER_AMOUNT_SHOULD_BE")}
                </Typography>
              )}
              {!canPlaceOrder && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {t("UI_TEXT.YOU_ARE_NOT_AUTHORIZED_ORDER")}{" "}
                  {t("UI_TEXT.PLEASE_CONTACT_THE_CUTOMER_CARE_FOR_FURTHER_DETAILS")}
                </Typography>
              )}
              {showDisclaimerCheckout && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography fontWeight="bold" sx={{ fontSize: "12px" }}>
                    {t("UI_TEXT.IMPORTANT_NOTICE_FINAL_SALE")}
                  </Typography>
                  <Typography sx={{ fontSize: "11px" }}>
                    {t("UI_TEXT.CUSTOM_MANUFACTURED_DISCLAIMER")}
                  </Typography>
                  <Typography mt={1} sx={{ fontSize: "11px" }}>
                    <strong>{t("UI_TEXT.NO_RETURNS")}</strong>{" "}
                    {t("UI_TEXT.FINAL_SALE")}
                  </Typography>
                </Alert>
              )}

              {/* ── CHANGE 4: Frequently Bought Together at the bottom ──── */}
              {recommendedProducts.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", mb: 1.5 }}>
                    <Typography variant="h6">
                      {t("UI_TEXT.FREQUENTLY_TOGETHER")}
                    </Typography>
                    <Box sx={{ background: "var(--primary-main)", height: "2px", mt: "2px", width: "90px" }} />
                  </Box>
                  <Box
                    className="cart-modal-scroll"
                    sx={{
                      maxHeight: "260px",
                      overflowY: "auto",
                      overflowX: "hidden",
                      pr: 0.5,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "10px",
                    }}
                  >
                    {recommendedProducts
                      .slice(0, 5)
                      .filter((p) => p !== null && p !== undefined)
                      .map((product) => (
                        <TopProductListItem
                          key={product.product_id}
                          product={product}
                          machine_id={product.machine_id}
                        />
                      ))}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CartModal;
